/**
 * Invite-Edge Matrix — canonical rules for who can invite whom.
 *
 * This is the client-side mirror of the server-side validate_invite_edge() function.
 * Both must stay in sync.
 */

export type ProgramRole = 'connector' | 'general_agent' | 'agent' | 'partner';
export type InviteTarget = ProgramRole | 'brand' | 'creator' | 'customer';

/**
 * Canonical invite-edge matrix.
 * Key = inviter's program_type, Value = set of roles they can invite.
 */
export const INVITE_EDGE_MATRIX: Record<ProgramRole, readonly InviteTarget[]> = {
  // Phase 2A: widened so every network role can recruit Brands and Creators.
  // Keep server-side public.validate_invite_edge() in sync.
  connector: ['general_agent', 'agent', 'brand', 'creator'],
  general_agent: ['agent', 'brand', 'creator'],
  agent: ['partner', 'brand', 'creator'],
  partner: ['brand', 'creator'],
} as const;

/**
 * Canonical role-entry routes. A role can ONLY be activated from its canonical route.
 */
export const CANONICAL_ROLE_ENTRY_ROUTES: Record<ProgramRole, string> = {
  connector: '/connector/onboarding',
  general_agent: '/general-agent/request',
  agent: '/agent/apply',
  partner: '/partner/onboarding',
} as const;

const PROGRAM_ROLE_ROUTE_PREFIXES = Object.freeze(
  Array.from(
    new Set(
      Object.values(CANONICAL_ROLE_ENTRY_ROUTES).map((route) => {
        const nextSlash = route.indexOf('/', 1);
        return nextSlash === -1 ? route : route.slice(0, nextSlash);
      }),
    ),
  ),
);

/**
 * True when a same-origin route belongs to one of the canonical
 * professional-program route namespaces.
 *
 * Prefixes are derived from CANONICAL_ROLE_ENTRY_ROUTES so Connector,
 * General Agent, Agent, and Partner cannot drift between shared Auth
 * consumers.
 */
export function isProgramRoleRoute(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;

  return PROGRAM_ROLE_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Check if a given inviter program_type is allowed to invite a target role.
 */
export function canInviteRole(inviterProgramType: string, targetRole: string): boolean {
  const allowed = INVITE_EDGE_MATRIX[inviterProgramType as ProgramRole];
  if (!allowed) return false;
  return allowed.includes(targetRole as InviteTarget);
}

/**
 * Validate that a role activation is happening from the correct canonical route.
 */
export function isCanonicalRoleEntry(programType: string, currentPath: string): boolean {
  const canonical = CANONICAL_ROLE_ENTRY_ROUTES[programType as ProgramRole];
  if (!canonical) return false;
  return currentPath === canonical || currentPath.startsWith(canonical);
}
